import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { io, type Socket } from "socket.io-client";
import { useQueryClient } from "@tanstack/react-query";
import { EVENT_INVALIDATION_MAP } from "@/lib/socketInvalidations";
import { env } from "@/config/env";
import { useAuth } from "@/api/auth";
import {
  FINAL_LISTING_INVALIDATION_EVENTS,
  invalidateFinalListingQueries,
} from "@/lib/finalListingQueryInvalidation";

interface SocketContextValue {
  socket: Socket | null;
}

const SocketContext = createContext<SocketContextValue>({ socket: null });

export function SocketProvider({ children }: { children: React.ReactNode }) {
  const { accessToken, principal } = useAuth();
  const queryClient = useQueryClient();
  const socketRef = useRef<Socket | null>(null);
  const [activeSocket, setActiveSocket] = useState<Socket | null>(null);

  useEffect(() => {
    if (!accessToken || !principal) {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
        setActiveSocket(null);
      }
      return;
    }

    const socket = io(`${env.apiBaseUrl}/events`, {
      transports: ["websocket", "polling"],
      auth: { token: accessToken },
      withCredentials: true,
      reconnection: true,
      reconnectionDelay: 2000,
    });

    socketRef.current = socket;
    setActiveSocket(socket);

    socket.on("connect", () => {
      if (env.debug) console.log("[socket] connected", socket.id);
      // Completion events can be missed during transport interruptions.
      // Re-read ready images and the queue without resubmitting provider work.
      void queryClient.invalidateQueries({ queryKey: ["page", "image-builder"] });
      void queryClient.invalidateQueries({ queryKey: ["assets", "listing-images"] });
    });

    socket.on("disconnect", (reason) => {
      if (env.debug) console.log("[socket] disconnected", reason);
    });

    socket.on("connect_error", (error) => {
      if (env.debug) console.error("[socket] connect error", error.message);
    });

    const events = new Set([
      ...Object.keys(EVENT_INVALIDATION_MAP),
      ...FINAL_LISTING_INVALIDATION_EVENTS,
    ]);
    events.forEach((event) => {
      socket.on(event, (payload: unknown) => {
        if (env.debug) console.log(`[socket] ${event}`, payload);
        invalidateFinalListingQueries(queryClient, event);
        const keys = EVENT_INVALIDATION_MAP[event] ?? [];
        for (let i = 0; i < keys.length; i += 2) {
          const queryKey = keys[i + 1] ? [keys[i], keys[i + 1]] : [keys[i]];
          queryClient.invalidateQueries({ queryKey });
        }
      });
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
      setActiveSocket(null);
    };
  }, [accessToken, principal, queryClient]);

  return (
    <SocketContext.Provider value={{ socket: activeSocket }}>
      {children}
    </SocketContext.Provider>
  );
}

export function useSocket(): SocketContextValue {
  return useContext(SocketContext);
}
